import { HStack, Spinner, Text, Tooltip, VStack } from "@chakra-ui/react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/sv";
import React, { MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { TooltipType, filters } from "../../../configs/schedule.config";
import useEscape from "../../../hooks/useEscape";
import Job, { DateOrString, JobScheduleItem, JobShift } from "../../../models/Job";
import TimeReport from "../../../models/TimeReport";
import { useScheduleSummaryData } from "../../../networking/job.hooks";
import { convertToSentenceCase } from "../../../utils/string.utils";
import AppText from "../../Core/AppText/AppText";
import Flex from "../../Layout/Flex";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";
import JobPopover from "./JobPopover";
import ShiftSalaryPopover from "./ShiftSalaryPopover";

dayjs.locale("sv");

/**
 * Height of a day.
 */
const DAY_HEIGHT = 22;

/**
 * Width of Y header which has job name or consultant name.
 */
const Y_HEADER_WIDTH = 200;

/**
 * Gap between day rows.
 */
const ROW_GAP = 12;

/**
 * Height of X header which holds day/dates.
 */
const X_HEADER_HEIGHT = 70;

/**
 * An approx width of a scroll bar, may be different depending on browser
 * but a rough value is fine.
 */
const SCROLL_BAR_WIDTH = 17;

/**
 * Height of canvas - the day grid excluding x and y headers.
 */
const CANVAS_HEIGHT = "80vh";

/**
 * Time in hours when a day starts. Is used to calculate position of shifts
 * within a day.
 */
const DAY_STARTS_AT = 6;

/**
 * Time in hours when a day ends. Is used to calculate position of shifts
 * within a day.
 */
const DAY_ENDS_AT = 20;

export type ZoomLevel = 1 | 2 | 3 | 4;

const ZOOM_LEVEL_DAY_WIDTH = {
	1: 30,
	2: 50,
	3: 90,
	4: 140,
};

export type EventType =
	| "shift-booked"
	| "shift-cancelled"
	| "shift-declined"
	| "shift-open"
	| "shift-extend-requested"
	// | "shift-extend-approved"
	| "shift-extend-declined"
	| "time-report-reported"
	| "time-report-declined"
	| "time-report-pending"
	| "absence";

export type ScheduleEvent = {
	id: string;
	start: DateOrString;
	end: DateOrString;
	type: EventType;
	job?: Job;
	shift?: JobShift;
	timeReport?: TimeReport;
};

export type ScheduleRow = {
	id: string;
	title: string | React.ReactElement;
	onClick: () => any;
	events: ScheduleEvent[];
};

export interface ScheduleProps {
	range: { startDate: Date; endDate: Date };
	rows?: ScheduleRow[];
	zoomLevel?: ZoomLevel;
	isLoading?: boolean;
	selectedFilter?: string;
	jobSchedules?: JobScheduleItem[];
	filterData?: {
		customerIdList: string[];
		contractIdList: string[];
		consultantIdList: string[];
	};
	shiftTooltipType?: TooltipType;
	shouldShowMonthLabel?: boolean;
	footer?: React.ReactNode;
}

export const Schedule: React.FC<ScheduleProps> = ({
	range,
	rows = [],
	zoomLevel = 2,
	isLoading = false,
	selectedFilter,
	filterData,
	shiftTooltipType = TooltipType.ShiftDetails,
	shouldShowMonthLabel = true,
	footer,
}) => {
	const xCtnRef = useRef<HTMLDivElement>(null);
	const yCtnRef = useRef<HTMLDivElement>(null);
	const canvasRef = useRef<HTMLDivElement>(null);
	const [activeEvent, setActiveEvent] = useState<{
		scheduleEvent: ScheduleEvent;
		pos: { x: number; y: number; pointTo: "top" | "left" };
	}>();

	function handleScrollCanvas(e: React.UIEvent<HTMLDivElement, UIEvent>) {
		if (xCtnRef.current) {
			xCtnRef.current.scrollLeft = e.currentTarget.scrollLeft;
		}
		if (yCtnRef.current) {
			yCtnRef.current.scrollTop = e.currentTarget.scrollTop;
		}
	}

	useEffect(() => {
		setActiveEvent(undefined);
	}, [zoomLevel]);

	useEscape({
		onEscape: () => {
			setActiveEvent(undefined);
		},
	});

	const days = useMemo(() => {
		let stepDate = dayjs(range.startDate);
		let days: {
			startOfDay: Dayjs;
			month: string;
			year: string;
			weekday: string;
			dayInMonth: string;
			isWeekend: boolean;
			isFirstDayOfMonth: boolean;
			id: string;
			date: Dayjs;
			isToday: boolean;
		}[] = [];

		while (dayjs(stepDate).isBefore(range.endDate)) {
			days.push({
				startOfDay: stepDate.startOf("day"),
				month: convertToSentenceCase(stepDate.format("MMMM")),
				year: stepDate.format("YYYY"),
				weekday: convertToSentenceCase(stepDate.format("ddd")),
				dayInMonth: stepDate.format("D"),
				isWeekend: [0, 6].includes(stepDate.day()),
				isFirstDayOfMonth: stepDate.date() === 1,
				id: stepDate.unix() + "",
				date: stepDate,
				isToday: stepDate.isSame(new Date(), "day"),
			});

			stepDate = stepDate.add(1, "day");
		}

		return days;
	}, [range]);

	const height = X_HEADER_HEIGHT + rows.length * (DAY_HEIGHT + ROW_GAP * 2) - 20;
	const width = ZOOM_LEVEL_DAY_WIDTH[zoomLevel] * days.length;

	function openPopover(e: MouseEvent<HTMLDivElement>, scheduleEvent: ScheduleEvent) {
		if (!canvasRef.current) return;
		const { left, top, width } = e.currentTarget.getBoundingClientRect();
		const canvasBRect = canvasRef.current.getBoundingClientRect();

		let pos: { x: number; y: number; pointTo: "top" | "left" };

		if (left < 400) {
			// Position popover on the right side to avoid clipping if
			// segment is on leftmost side in canvas
			pos = {
				x: left + width + 42 - canvasBRect.left + canvasRef.current.scrollLeft,
				y: top - canvasBRect.top + canvasRef.current.scrollTop - 50,
				pointTo: "left",
			};
		} else {
			pos = {
				x: left + width / 2 - canvasBRect.left + canvasRef.current.scrollLeft,
				y: top - canvasBRect.top + canvasRef.current.scrollTop,
				pointTo: "top",
			};
		}

		setActiveEvent({
			scheduleEvent,
			pos,
		});
	}

	return isLoading ? (
		<LoadingIndicator />
	) : (
		<ShiftCalendarStyles $dayWidth={ZOOM_LEVEL_DAY_WIDTH[zoomLevel]}>
			<div className="y-hd-ctn" style={{ width: Y_HEADER_WIDTH }} ref={yCtnRef}>
				<div style={{ height }}>
					{rows.map((row) => (
						<div key={row.id} className="row-title">
							{typeof row.title === "string" ? (
								<AppText textAlign="right">{row.title}</AppText>
							) : (
								row.title
							)}
						</div>
					))}
					{rows.length === 0 && (
						<div className="mr-4">
							<div className="h-7 mt-2 bg-gray-300/60 rounded-xl text-black/30 text-sm"></div>
						</div>
					)}
				</div>
			</div>
			<div className="x-hd-ctn" ref={xCtnRef}>
				<Flex alignItems="flex-end" width={width + "px"}>
					{days.map((d) =>
						selectedFilter === filters[0].id ? (
							<Tooltip
								key={d.id}
								bg="#FFFFFF"
								color="#666666"
								openDelay={500}
								borderRadius={"5px"}
								border={"1px solid #CCCCCC"}
								label={<TooltipContent date={d.date.format().toString()} filterData={filterData} />}
							>
								<div className="day-hd">
									<AppText
										size="s"
										weight="medium"
										textAlign="center"
										color={d.isWeekend ? "danger" : undefined}
									>
										{zoomLevel === 1 ? d.weekday.substring(0, 2) : d.weekday}
										<br />
										{d.dayInMonth}
									</AppText>
									{d.isFirstDayOfMonth && shouldShowMonthLabel && (
										<AppText className="month-title" color="grayText" weight="medium" size="l">
											{d.month} {d.year}
										</AppText>
									)}
								</div>
							</Tooltip>
						) : (
							<div className="day-hd" key={d.id}>
								<AppText
									size="s"
									weight="medium"
									textAlign="center"
									color={d.isWeekend ? "danger" : undefined}
								>
									{zoomLevel === 1 ? d.weekday.substring(0, 2) : d.weekday}
									<br />
									{d.dayInMonth}
								</AppText>
								{d.isFirstDayOfMonth && shouldShowMonthLabel && (
									<AppText className="month-title" color="grayText" weight="medium" size="l">
										{d.month} {d.year}
									</AppText>
								)}
							</div>
						)
					)}
				</Flex>
			</div>
			<div className="scroll" onScroll={handleScrollCanvas} ref={canvasRef}>
				<div style={{ width, height }}>
					<Flex className="canvas" direction="column">
						{rows.map((row) => (
							<Flex className="day-row" key={row.id} gap="2px" alignItems="center">
								{days.map((d) => (
									<div key={d.id} className={`day ${d.isToday ? "today" : ""}`}>
										{row.events
											.filter((s) => dayjs(s.start).isSame(d.date, "day"))
											.map((s) => {
												const shiftStackLength = row.events.filter((re) =>
													dayjs(re.start).isSame(d.date, "day")
												).length;
												return (
													<div
														key={s.id}
														style={{
															display: "flex !important",
															flexDirection: "column",
															height: `${DAY_HEIGHT / shiftStackLength}px`,
														}}
													>
														<StyledShift
															shiftHeight={DAY_HEIGHT / shiftStackLength}
															className={`event event-${s.type}`}
															key={s.id}
															style={calcCoordinates(s, ZOOM_LEVEL_DAY_WIDTH[zoomLevel])}
															onClick={(e) => openPopover(e, s)}
														/>
													</div>
												);
											})}
									</div>
								))}
							</Flex>
						))}
					</Flex>

					{activeEvent && (
						<div
							className="event-popover"
							style={{
								top: activeEvent.pos.y + DAY_HEIGHT + 10,
								left: activeEvent.pos.x - 32,
							}}
						>
							{shiftTooltipType === TooltipType.ShiftDetails && (
								<JobPopover
									pointTo={activeEvent.pos.pointTo}
									scheduleEvent={activeEvent.scheduleEvent}
									onClose={() => setActiveEvent(undefined)}
								/>
							)}
							{shiftTooltipType === TooltipType.ShiftSalaryDetails && (
								<ShiftSalaryPopover
									pointTo={activeEvent.pos.pointTo}
									scheduleEvent={activeEvent.scheduleEvent}
									onClose={() => setActiveEvent(undefined)}
								/>
							)}
						</div>
					)}
					{rows.length === 0 && (
						<div className="mr-4">
							<div className="h-7 mt-2 bg-white/75 rounded-sm px-4 py-1 text-sm text-black/30">
								Det finns ingen data
							</div>
						</div>
					)}
				</div>
				{footer}
			</div>
		</ShiftCalendarStyles>
	);
};

const ShiftCalendarStyles = styled.div<{ $dayWidth: number }>`
	width: 100%;
	position: relative;

	.scroll {
		position: absolute;
		left: ${Y_HEADER_WIDTH}px;
		width: calc(100% - ${Y_HEADER_WIDTH}px);
		height: ${CANVAS_HEIGHT};
		overflow: scroll;
	}

	.canvas {
		/* min-height: 85vh; */
	}

	.x-hd-ctn {
		padding-top: 30px;
		padding-left: ${Y_HEADER_WIDTH}px;
		height: ${X_HEADER_HEIGHT}px;
		overflow: hidden;
		width: 100%;
	}

	.y-hd-ctn {
		position: absolute;
		top: ${X_HEADER_HEIGHT}px;
		overflow: hidden;
		height: calc(${CANVAS_HEIGHT} - ${SCROLL_BAR_WIDTH}px);

		.row-title {
			width: ${Y_HEADER_WIDTH}px;
			padding-right: ${ROW_GAP}px;
			height: ${DAY_HEIGHT + ROW_GAP * 2}px;
			display: flex;
			flex-direction: column;
			justify-content: center;
		}
	}

	.day-hd {
		width: ${(props) => props.$dayWidth}px;
		text-align: center;
		position: relative;
		cursor: pointer;

		&:hover {
			background-color: #d8e5ea;
			color: hsl(var(--action));
		}

		.month-title {
			position: absolute;
			top: -30px;
			left: 10px;
			width: 150px;
		}
	}

	.day-row {
		margin: ${ROW_GAP}px 0;

		.day {
			background-color: white;
			width: ${(props) => props.$dayWidth}px;
			height: ${DAY_HEIGHT}px;
			position: relative;

			&.today {
				background-color: #d0e1d8;
			}

			.event {
				position: absolute;
				width: 10px;
				/* height: ${DAY_HEIGHT}px; */
				cursor: pointer;
				transition: transform 0.125s;

				&.event-shift-booked,
				&.event-time-report-reported,
				&.event-time-report-pending {
					background-color: #72c872;
				}

				&.event-shift-open {
					background-color: #999;
				}
				&.event-shift-extend-declined,
				&.event-shift-cancelled,
				&.event-shift-declined {
					/* background-color: #da595d; */
					border: 2px solid #da595d;
				}

				&.event-time-report-declined {
					background-color: #da595d;
				}

				&.event-absence {
					/* background-color: #ffff00; */
					background-color: #da595d;
				}

				&.event-shift-extend-requested {
					background-color: orange;
				}

				&:hover {
					filter: brightness(80%);
					transform: scaleY(1.15);
				}
			}
		}
	}

	.event-popover {
		position: absolute;
		z-index: 5;
	}
`;

const StyledShift = styled.div<{ shiftHeight: number }>`
	height: ${(props) => props.shiftHeight}px;
`;

// UTILS

function calcCoordinates(event: ScheduleEvent, dayWidth: number) {
	// Absences should span the full day width
	if (event.type === "absence") {
		return {
			left: "0px",
			width: dayWidth + "px",
		};
	}

	// For shifts and time reports, calculate position based on time
	const pxPerHour = dayWidth / (DAY_ENDS_AT - DAY_STARTS_AT);
	const startHour = dayjs(event.start).hour();
	const endHour = dayjs(event.end).hour();
	const numHours = Math.min(DAY_ENDS_AT, endHour) - Math.max(DAY_STARTS_AT, startHour);

	let left = 0;

	if (startHour < DAY_STARTS_AT) left = 0;
	else if (startHour > DAY_ENDS_AT) left = dayWidth - 2; /* show a notch of it at least */
	else left = (startHour - DAY_STARTS_AT) * pxPerHour;

	return {
		left: left + "px",
		width: Math.min(Math.max(pxPerHour * numHours, 4), dayWidth) + "px", // Set to at least 4px to have a shot at seeing it and max same as day width
	};
}

interface TooltipContentProp {
	date: string;
	filterData?: {
		customerIdList: string[];
		contractIdList: string[];
		consultantIdList: string[];
	};
}

const TooltipContent: React.FC<TooltipContentProp> = ({ date, filterData }: TooltipContentProp) => {
	const { absents, booked, unbooked, denied, isLoading } = useScheduleSummaryData(
		dayjs(date).startOf("day").toISOString(),
		dayjs(date).endOf("day").toISOString(),
		filterData?.customerIdList,
		filterData?.contractIdList,
		filterData?.consultantIdList
	);
	return (
		<>
			{
				<VStack minW={"150px"} p={2}>
					<HStack
						width={"100%"}
						justify={"space-between"}
						borderBottom={"1px solid #CCCCCC"}
						py={1}
					>
						<Text fontWeight={400} fontSize={"10px"} color={"#666666"}>
							Bokade pass:
						</Text>
						{!!isLoading ? (
							<Spinner size="xs" color="#666666" />
						) : (
							<Text fontWeight={600} fontSize={"10px"} color={"#333333"}>
								{booked}
							</Text>
						)}
					</HStack>
					<HStack
						width={"100%"}
						justify={"space-between"}
						borderBottom={"1px solid #CCCCCC"}
						py={1}
					>
						<Text fontWeight={400} fontSize={"10px"} color={"#666666"}>
							Obokade pass:
						</Text>
						{!!isLoading ? (
							<Spinner size="xs" color="#666666" />
						) : (
							<Text fontWeight={600} fontSize={"10px"} color={"#333333"}>
								{unbooked}
							</Text>
						)}
					</HStack>
					<HStack
						width={"100%"}
						justify={"space-between"}
						borderBottom={"1px solid #CCCCCC"}
						py={1}
					>
						<Text fontWeight={400} fontSize={"10px"} color={"#666666"}>
							Frånvarande konsulter:
						</Text>
						{!!isLoading ? (
							<Spinner size="xs" color="#666666" />
						) : (
							<Text fontWeight={600} fontSize={"10px"} color={"#333333"}>
								{absents}
							</Text>
						)}
					</HStack>
					<HStack width={"100%"} justify={"space-between"} py={1}>
						<Text fontWeight={400} fontSize={"10px"} color={"#666666"}>
							Nekade jobb:
						</Text>
						{!!isLoading ? (
							<Spinner size="xs" color="#666666" />
						) : (
							<Text fontWeight={600} fontSize={"10px"} color={"#333333"}>
								{denied}
							</Text>
						)}
					</HStack>
				</VStack>
			}
		</>
	);
};
